function debounce(fn, delay) {
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), delay);
  };
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();

      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      };

      this.mql = window.matchMedia('(min-width: 750px)');
      this.productInfo = document.getElementById(`ProductInfo-${this.dataset.section}`);
      this.prependMedia = this.dataset.disablePrepend !== 'true';
      this.filteringOption = this.dataset.filteringOption;

      if (this.productInfo && Shopify.postLinksRetry) this.productInfo.initShareLinks();
      if (!this.elements.viewer) return;

      // Event listeners
      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 300));
      if (this.elements.thumbnails) {
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((thumb) => {
          const button = thumb.querySelector('button');
          if (!button) return;

          button.addEventListener('click', (e) => {
            e.preventDefault();
            const targetId = thumb.dataset.target;
            // If your Liquid prefixes the IDs like "SectionID-MediaID", we normalize it:
            const normalizedId = targetId.startsWith(`${this.dataset.section}-`)
              ? targetId
              : `${this.dataset.section}-${targetId}`;

            this.setActiveMedia(normalizedId, false);
          });
        });
      }

      // Accessibility & layout adjustments
      if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
    }

    /**
     * Handle slider change events (auto-sync thumbnails)
     */
    onSlideChanged(event) {
      const mediaId = event.detail.currentElement.dataset.mediaId;
      const thumbnail = this.elements.thumbnails?.querySelector(`[data-target="${mediaId}"]`);
      if (thumbnail) this.setActiveThumbnail(thumbnail);
    }

    /**
     * Switch active media when thumbnail clicked or variant changes
     */
    setActiveMedia(mediaId, prepend = false, filtering = false, currentVariant = null) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
      if (!activeMedia) return;

      // Variant filtering
      if (filtering && currentVariant) {
        const variantValue = currentVariant[this.filteringOption];
        this.querySelectorAll('.product__media-item, .thumbnail-list__item, .slider-counter__link').forEach(slide => {
          if (slide.dataset.alt === variantValue || slide.dataset.alt === 'always_display') {
            slide.classList.remove('hidden');
          } else {
            slide.classList.add('hidden');
          }
        });
      }

      // Activate media
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach(el => el.classList.remove('is-active'));
      activeMedia.classList.add('is-active');

      // Move selected image to front if required
      if (prepend && this.prependMedia) {
        const mediaParent = activeMedia.parentElement;
        mediaParent.prepend(activeMedia);

        const activeThumb = this.elements.thumbnails?.querySelector(`[data-target="${mediaId}"]`);
        if (activeThumb) activeThumb.parentElement.prepend(activeThumb);

        if (this.elements.viewer.slider) this.elements.viewer.resetPages?.();
      }

      // Scroll behavior for mobile or no thumbnails
      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        } else {
          activeMedia.scrollIntoView({ behavior: 'smooth' });
        }
      }, 50);

      // Play media if it's a video
      this.playActiveMedia(activeMedia);

      // Re-init slider if filtering changed
      if (filtering && currentVariant) {
        this.elements.viewer.initPages?.();
        this.elements.viewer.update?.();
      }

      // Sync thumbnail state
      if (this.elements.thumbnails) {
        const activeThumb = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumb);
        this.announceLiveRegion(activeMedia, activeThumb?.dataset.mediaPosition || '');
      }
    }

    /**
     * Mark thumbnail as active and scroll it into view
     */
    setActiveThumbnail(thumbnail) {
      if (!thumbnail || !this.elements.thumbnails) return;
      this.elements.thumbnails.querySelectorAll('button').forEach(btn => btn.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);

      const slider = this.elements.thumbnails.slider || this.elements.thumbnails;
      if (this.elements.thumbnails.vertical) {
        slider.scrollTo({ top: thumbnail.offsetTop - 20, behavior: 'smooth' });
      } else {
        slider.scrollTo({ left: thumbnail.offsetLeft - 20, behavior: 'smooth' });
      }
    }

    /**
     * Screen reader announcement for new image
     */
    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('img');
      if (!image || !this.elements.liveRegion) return;

      this.elements.liveRegion.setAttribute('aria-hidden', 'false');
      this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
      setTimeout(() => this.elements.liveRegion.setAttribute('aria-hidden', 'true'), 2000);
    }

    /**
     * Play video or 3D media when active
     */
    playActiveMedia(activeItem) {
      window.pauseAllMedia?.();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia && typeof deferredMedia.loadContent === 'function') deferredMedia.loadContent(false);
    }

    /**
     * Prevent sticky header flicker when scrolling
     */
    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      this.stickyHeader?.dispatchEvent(new Event('preventHeaderReveal'));
    }

    /**
     * Remove unnecessary list semantics (for accessibility)
     */
    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems?.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
